import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import deemaLogo from '../assets/img/bnpl/deema.png';
import talyLogo from '../assets/img/bnpl/taly.svg';
import heroImg from '../assets/img/bnpl/bnpl-hero.jpg';
import knetIcon from '../assets/img/knet.png';
import masterIcon from '../assets/img/mastercard_icon.png';
import applePayIcon from '../assets/img/applepay.png';

/**
 * Custom on-site BNPL messaging widget for the product & cart pages.
 *
 * Replaces the old single-provider Taly widget with one combined bar showing
 * BOTH installment providers (Deema + Taly). Clicking "Learn more" opens a
 * popup modelled on wibi.com.kw's payment-methods modal — same layout and copy,
 * but recoloured to the site's own brand pink (#f91f7a) instead of wibi's purple.
 *
 * Assets (deema logo, taly logo, hero photo) were pulled from wibi so the
 * provider branding stays consistent; only the accent colour is ours.
 *
 * BNPL is KWD-only and requires a minimum order — mirror the providers' rules.
 */

// Brand palette (matches .btn_primary on the storefront).
const BRAND = '#f91f7a';
const BRAND_DARK = '#b30a59';
const INK = '#1a1a2e';

// Installments + minimum order (KWD). Providers require ≥ 10 KWD.
const SPLITS = 4;
const MIN_KWD = 10;

const money = (n) => Number(n || 0).toFixed(3);

function BnplModal({ perPayment, onClose }) {
  // Lock body scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const steps = [
    'Choose your preferred installment provider (Deema or Taly) at checkout.',
    'Enter your information and add your debit or credit card.',
    'Depending on your plan, you may or may not make a down payment.',
    "We'll send you a reminder when your next payment is due.",
    `For Deema / Taly the minimum cart value must be KWD ${MIN_KWD} or above. 🛒`,
  ];

  // Render through a portal to <body> so the fixed overlay escapes any ancestor
  // stacking context on the product page (sticky header / scroll effects) that
  // was letting Related Products paint on top of the popup.
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483000,
        background: 'rgba(0,0,0,.55)', display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center',
        padding: '24px 12px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 420, background: '#fff',
          borderRadius: 22, overflow: 'hidden', margin: 'auto',
          boxShadow: '0 24px 60px rgba(0,0,0,.28)', fontFamily: 'inherit',
        }}
      >
        {/* Header: both provider logos + close */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={deemaLogo} alt="Deema" style={{ height: 26, width: 'auto' }} />
            <img src={talyLogo} alt="Taly" style={{ height: 26, width: 'auto' }} />
          </div>
          <button
            onClick={onClose} aria-label="Close"
            style={{ border: 0, background: 'transparent', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#333' }}
          >×</button>
        </div>

        {/* Hero */}
        <div style={{ padding: '0 14px' }}>
          <div style={{
            position: 'relative', borderRadius: 16, overflow: 'hidden',
            minHeight: 200, display: 'flex', alignItems: 'flex-end',
            backgroundImage: `linear-gradient(180deg, rgba(249,31,122,.10) 0%, rgba(179,10,89,.92) 100%), url(${heroImg})`,
            backgroundSize: 'cover', backgroundPosition: 'center 22%',
          }}>
            <div style={{ padding: 18, color: '#fff' }}>
              <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1 }}>Get more time to pay</div>
              <div style={{ fontSize: 14, marginTop: 6, opacity: .95 }}>Split your purchase in up to {SPLITS} payments</div>
            </div>
          </div>
        </div>

        {/* Summary card */}
        <div style={{ padding: 14 }}>
          <div style={{ border: '1px solid #eee', borderRadius: 14, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, color: INK }}>{SPLITS} payments</div>
              <div style={{ color: '#16a34a', fontSize: 13, marginTop: 2 }}>No interest. No fees.</div>
            </div>
            <div style={{ fontWeight: 800, color: INK }}>KWD {money(perPayment)}<span style={{ fontWeight: 500, color: '#888', fontSize: 13 }}>/mo</span></div>
          </div>
        </div>

        {/* How it works */}
        <div style={{ padding: '0 14px 14px' }}>
          <div style={{ border: '1px solid #eee', borderRadius: 14, padding: '16px 16px 6px' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: INK, marginBottom: 12 }}>How it works</div>
            {steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                <div style={{
                  flex: '0 0 24px', height: 24, borderRadius: '50%', background: BRAND,
                  color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>{i + 1}</div>
                <div style={{ fontSize: 14, color: '#333', lineHeight: 1.35 }}>{s}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust cards */}
        <div style={{ padding: '0 14px' }}>
          {[
            { t: 'Trusted by thousands', d: 'Shoppers across Kuwait split their purchases and pay their way.' },
            { t: 'Shop with confidence', d: 'Buyer protection is included with every secure payment.' },
          ].map((c, i) => (
            <div key={i} style={{ border: '1px solid #eee', borderRadius: 14, padding: 16, marginBottom: 12, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: '0 0 34px', height: 34, borderRadius: '50%', background: `${BRAND}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND, fontSize: 18 }}>
                {i === 0 ? '👝' : '✓'}
              </div>
              <div>
                <div style={{ fontWeight: 700, color: INK }}>{c.t}</div>
                <div style={{ fontSize: 13, color: '#777', marginTop: 2 }}>{c.d}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer button */}
        <div style={{ padding: '4px 14px 12px' }}>
          <button
            onClick={onClose}
            style={{
              width: '100%', border: 0, borderRadius: 14, padding: '14px 16px',
              background: INK, color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
            }}
          >Continue shopping</button>
        </div>

        {/* Accepted payment methods */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '0 14px 18px', flexWrap: 'wrap' }}>
          {[
            <span key="visa" style={{ fontStyle: 'italic', fontWeight: 800, fontSize: 13, color: '#1a1f71', letterSpacing: '.3px' }}>VISA</span>,
            <img key="mc" src={masterIcon} alt="Mastercard" style={{ height: 16, width: 'auto' }} />,
            <img key="knet" src={knetIcon} alt="KNET" style={{ height: 16, width: 'auto' }} />,
            <img key="apay" src={applePayIcon} alt="Apple Pay" style={{ height: 15, width: 'auto' }} />,
          ].map((el, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 46, height: 30, padding: '0 8px',
              border: '1px solid #eee', borderRadius: 8, background: '#fff',
            }}>{el}</span>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function BnplWidget({ price, paymentPage }) {
  const [open, setOpen] = useState(false);
  const total = parseFloat(price);

  if (!total || total <= 0) return null;

  const perPayment = total / SPLITS;

  return (
    <>
      {/* Deterministic 2-row layout so it reads the same in a wide or a narrow
          (product-column) container — no awkward flex wrapping. */}
      <div style={{
        border: '1px solid #e6e6ea', borderRadius: 12, padding: '12px 16px',
        background: '#fff',
      }}>
        <div style={{ fontSize: 14, color: '#333', lineHeight: 1.4 }}>
          {SPLITS} interest-free payments of KWD <strong>{money(perPayment)}</strong>.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={deemaLogo} alt="Deema" style={{ height: 20, width: 'auto' }} />
            <img src={talyLogo} alt="Taly" style={{ height: 20, width: 'auto' }} />
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            style={{ border: 0, background: 'transparent', color: BRAND_DARK, fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline', whiteSpace: 'nowrap' }}
          >Learn more</button>
        </div>
      </div>

      {open && <BnplModal perPayment={perPayment} onClose={() => setOpen(false)} />}
    </>
  );
}
